import { AddonDetails } from "../../api/entities/Addon";
import {
  EuiButton,
  EuiCard,
  EuiDatePicker,
  EuiFieldNumber,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { GetAddonPrice } from "../../utils/ProductUtils";
import { NumberToCurrency } from "../../utils/Formatters";
import React, { useState } from "react";
import moment from "moment";
import { useDebounceEffect } from "utils/useDebounceEffect";

export interface IAddonPurchaseCard {
  addon: AddonDetails;
  quantity?: number;
  productGroupId: string;
  onCustomizableAddonUpdated?: (
    addonDetailsId: string,
    startDate?: string,
    contractLength?: number,
  ) => void;
  onRemove?: () => void;
}

export const AddonPurchaseCard: React.FC<IAddonPurchaseCard> = ({
  addon,
  quantity,
  productGroupId,
  onCustomizableAddonUpdated,
  onRemove,
}) => {
  const [startDate, setStartDate] = useState<moment.Moment | null | undefined>(
    addon.customizableStartDateAndContractLength
      ? addon.startDate
        ? moment(addon.startDate)
        : moment()
      : undefined,
  );
  const [contractLength, setContractLength] = useState<number | undefined>(
    addon.customizableStartDateAndContractLength
      ? (addon.contractLength ?? 1)
      : undefined,
  );

  let price = GetAddonPrice(addon, productGroupId);
  price = price * (quantity || 1);

  useDebounceEffect(
    100,
    () => {
      if (!addon.customizableStartDateAndContractLength) {
        return;
      }

      if (
        (!startDate && !contractLength) ||
        (startDate?.format("MM/DD/YYYY") === addon.startDate &&
          contractLength === addon.contractLength)
      ) {
        return;
      }

      onCustomizableAddonUpdated?.(
        addon.id,
        startDate?.format("MM/DD/YYYY"),
        contractLength,
      );
    },
    [
      addon.id,
      addon.customizableStartDateAndContractLength,
      addon.startDate,
      addon.contractLength,
      onCustomizableAddonUpdated,
      startDate,
      contractLength,
    ],
  );

  return (
    <EuiCard
      hasBorder={true}
      title={
        <EuiTitle size={"s"}>
          <h2>
            {addon.name}
            {quantity && quantity > 1 ? ` x ${quantity}` : ""}
          </h2>
        </EuiTitle>
      }
      description={
        <EuiText size={"s"}>
          <p>{addon.description}</p>
        </EuiText>
      }
      footer={
        <EuiFlexGroup>
          <EuiFlexItem />
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onRemove} color={"danger"}>
              Remove
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      }
      style={{ width: 400 }}
    >
      <EuiSpacer />
      <EuiFlexGroup direction="column">
        <EuiFlexItem>
          {price > 0 && <EuiText>{NumberToCurrency(price)}</EuiText>}
        </EuiFlexItem>
        {addon.customizableStartDateAndContractLength && (
          <EuiFlexItem>
            <EuiFormRow label="Start Date">
              <EuiDatePicker
                selected={startDate}
                minDate={moment()}
                onChange={(date) => setStartDate(date)}
                startDate={moment()}
                aria-label="Start date"
              />
            </EuiFormRow>
            <EuiFormRow label="Contract Length">
              <EuiFieldNumber
                value={contractLength}
                onChange={(val) =>
                  setContractLength(parseInt(val.target.value))
                }
                step={1}
                min={1}
                max={12}
                append={"months"}
              />
            </EuiFormRow>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiCard>
  );
};
